import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../redux/productSlice";
import { addToCart, decrementCartItem, clearCart } from "../../redux/cartSlice";
import { fetchAllCommercePartners } from "../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../redux/productRatingSlice";
import { logout } from "../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../redux/notificationsSlice";
import { CurrencyContext } from "../../GLOBAL/CurrencyContext";
import appLogo from "../../assets/app/logo.png";

import {
  Search, ShoppingBag, X, ChevronRight, Plus, Minus,
  Heart, User, ShoppingCart, MapPin, Globe,
  ChevronDown, ArrowRight,
  Package, Home, ShoppingBasket, Star, LayoutGrid, Check, AlertTriangle,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   VERTICAL CATEGORY SOURCES
   Baby / Mommy Care / Maternity are intentionally NOT imported
   anywhere — Fashion only pulls grown-up ranges (Clothing,
   Accessories, Jewellery, Shoes, Bags); Beauty excludes the
   kids/baby subtree.
═══════════════════════════════════════════════════════════ */
import { fastfoodCategories, fastfoodSubcategories, fastfoodProducts } from "../../CATEGORIES/FoodCategories";
import { groceryCategories, grocerySubcategories, groceryProducts } from "../../CATEGORIES/GroceryCategories";
import { farmCategories, farmSubcategories, farmProducts } from "../../CATEGORIES/FarmCategories";
import { giftsCategories, giftsSubcategories, giftsProducts } from "../../CATEGORIES/GiftsCategories";

import {
  GenderCategories,
  FashionCategories,
  FashionClothing,
  FashionAccessories,
  FashionSunglasses,
  FashionHats,
  FashionWatches,
  FashionJewellery,
  FashionNecklaces,
  FashionRings,
  FashionShoes,
  FashionBags,
} from "../../CATEGORIES/FashionCategories";
import {
  BabyMaternityCategories,
  BabyClothing,
  BabyShoes,
  BabyCare,
  MommyCare,
  BabyFeeding,
  BabyBedtime,
  BabyTravel,
  BabyToys,
  BabyNursery,
  Maternity,
  BabyHealthSafety,
  ActivityEntertainment,
  BabyAccessories,
  PottyTraining,
  Gifts as MaternityGifts,
  BabyStationery,
  CarSeatsAccessories,
  Clothing as MaternityClothing,
  BabyTeething,
  AllBabyMaternityProducts,
} from "../../CATEGORIES/BabyCategories";
import {
  BeautyCategories,
  BeautySkincareSubcategories,
  BeautyMakeupSubcategories,
  BeautyHaircareSubcategories,
  BeautyFragranceSubcategories,
  BeautyBodySubcategories,
  BeautyNailsSubcategories,
  BeautyToolsSubcategories,
  BeautyMensGroomingSubcategories,
  BeautyWellnessSubcategories,
  BeautySetsSubcategories,
  AllBeautyProducts,
} from "../../CATEGORIES/BeautyCategories";

// ── everything below is wired but OFF by default in VERTICALS (see toggle list) ──
import { electronicsCategories, electronicsSubcategories, electronicsProducts } from "../../CATEGORIES/ElectronicsCategories";
import { idToysCategories, idToysSubcategories, idToysProducts } from "../../CATEGORIES/ToysCategories";
import { idGamingCategories, idGamingSubcategories, idGamingProducts } from "../../CATEGORIES/GamingCategories";
import { sportsCategories, sportsSubcategories, sportsProducts } from "../../CATEGORIES/SportsCategories";
import { idArtCraftsCategories, idArtCraftsSubcategories, idArtCraftsProducts } from "../../CATEGORIES/ArtCraftsCategories";
import { idAviationCategories, idAviationSubcategories, idAviationProducts } from "../../CATEGORIES/AviationCategories";
import { automotiveCategories, automotiveSubcategories, automotiveProducts } from "../../CATEGORIES/AutomotiveCategories";
import { toolsCategories, toolsSubcategories, toolsProducts } from "../../CATEGORIES/ToolsCategories";
import { idPetsCategories, idPetsSubcategories, idPetsProducts } from "../../CATEGORIES/PetsCategories";
import { idFurnitureCategories, idFurnitureSubcategories, idFurnitureProducts } from "../../CATEGORIES/FurnitureCategories";
import { kitchenCategories, kitchenSubcategories, kitchenProducts } from "../../CATEGORIES/KitchenCategories";
import { idLuggageCategories, idLuggageSubcategories, idLuggageProducts } from "../../CATEGORIES/LuggageCategories";
import { idBooksCategories, idBooksSubcategories, idBooksProducts } from "../../CATEGORIES/BooksCategories";

import {
  Sizes,
  ShoeSizes,
  productColors,
} from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

/* ═══════════════════════════════════════════════════════════
   sphere helper — colour-swatch variant picker
═══════════════════════════════════════════════════════════ */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 28%, transparent 52%),`,
    `radial-gradient(circle at 68% 72%, rgba(0,0,0,0.30) 0%, transparent 55%),`,
    `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`,
  ].join(" ");
}

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as AtlasHome
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  headerBg: "#FFFFFF",

  dark: "#1B0E06",
  black: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
  divider: "#F3E6CE",
  danger: "#C8202E",
};

const T = {
  pageBg:   HEART.bg,
  cardBg:   HEART.panelBg,
  cardBg2:  HEART.goldSoft,

  black:    HEART.dark,
  blackMid: HEART.textMuted,
  white:    HEART.panelBg,
  offwhite: HEART.dark,
  muted:    HEART.textMuted,
  mutedDim: HEART.textFaint,

  red:      HEART.red,
  redDim:   HEART.redSoft,
  redGlow:  "rgba(200,32,46,0.30)",

  border:       HEART.border,
  borderStrong: HEART.gold,

  grad:  `linear-gradient(135deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  gradH: `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,

  outline: `1.5px solid ${HEART.border}`,
};

const GRAD   = T.grad;
const GRAD_H = T.gradH;
const RGBA_RED = (a: number) => `rgba(200,32,46,${a})`;

const PRODUCT_ACCENT_COLORS = [T.black, T.red];
const productAccentColor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return PRODUCT_ACCENT_COLORS[hash % PRODUCT_ACCENT_COLORS.length];
};

/* ═══════════════════════════════════════════════════════════
   ICON RENDERER — handles BOTH icon shapes seen across the
   category files:
   • Fashion/Beauty: icon is already a rendered <Icon/> node
   • Grocery/Fastfood/Gifts/etc: icon is an unrendered component
     reference + a separate `color` field
═══════════════════════════════════════════════════════════ */
function renderCatIcon(item: any, size = 18, fallbackColor?: string) {
  if (!item) return null;
  const raw = item.icon;
  if (!raw) return null;
  if (React.isValidElement(raw)) return raw;
  // Component reference — covers plain function components AND
  // forwardRef/memo-wrapped ones (e.g. lucide-react icons), whose
  // typeof is "object", not "function" — that mismatch is why
  // grocery/gifts/fastfood-style icon refs weren't rendering before.
  const isComponentRef =
    typeof raw === "function" ||
    (typeof raw === "object" && raw !== null && "$$typeof" in raw);
  if (isComponentRef) {
    const Icon = raw as React.ElementType;
    return <Icon size={size} color={fallbackColor ?? item.color ?? HEART.dark} />;
  }
  return null;
}

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTE LOOKUPS (flattened for the variant modal)
═══════════════════════════════════════════════════════════ */
const colorPalette = productColors[0]?.values ?? [];
const sizePalette   = [...Sizes, ...ShoeSizes];

/* ═══════════════════════════════════════════════════════════
   ADULT-ONLY DERIVED FASHION/BEAUTY SETS — no baby/maternity
═══════════════════════════════════════════════════════════ */
const FASHION_ADULT_CATEGORIES = FashionCategories.filter(c => c.id >= 11 && c.id <= 15);
const FASHION_CLOTHING_ADULT = FashionClothing.filter(c => ![1101, 1102, 1103].includes(c.id));
const FASHION_ADULT_SUBCATEGORIES = [
  ...FASHION_CLOTHING_ADULT,
  ...FashionAccessories,
  ...FashionJewellery,
  ...FashionShoes,
  ...FashionBags,
];
const FASHION_ADULT_PRODUCTS = [
  ...FashionSunglasses,
  ...FashionHats,
  ...FashionWatches,
  ...FashionNecklaces,
  ...FashionRings,
];
const BEAUTY_ADULT_CATEGORIES = BeautyCategories.filter(c => c.id !== 210); // drop Kids & Baby Beauty
const BEAUTY_ADULT_SUBCATEGORIES = [
  ...BeautySkincareSubcategories,
  ...BeautyMakeupSubcategories,
  ...BeautyHaircareSubcategories,
  ...BeautyFragranceSubcategories,
  ...BeautyBodySubcategories,
  ...BeautyNailsSubcategories,
  ...BeautyToolsSubcategories,
  ...BeautyMensGroomingSubcategories,
  ...BeautyWellnessSubcategories,
  ...BeautySetsSubcategories,
];

/* ═══════════════════════════════════════════════════════════════════════
   ⭐ THE MASTER TOGGLE LIST ⭐
   -------------------------------------------------------------------
   ONE array. ONE line per vertical. Comment a line out (//) to hide
   that whole category everywhere — toggle bar, icon rail, shop-by-
   category grid, sidebar tree. Uncomment to bring it back. Nothing
   else in this file needs to change.

   `accent` is only used as a fallback color (pill background, icon
   bg when a category has no image/color of its own) — every source
   file's own per-category `color`/`image` still wins when present.
═══════════════════════════════════════════════════════════════════════ */
type Vertical = {
  slug: string;
  label: string;
  accent: string;
  roots: any[];
  subs: any[];
  products: any[];
};

const VERTICALS: Vertical[] = [
  { slug: "grocery",   label: "Grocery",    accent: "#FFB300", roots: groceryCategories,  subs: grocerySubcategories,  products: groceryProducts },
  { slug: "gifts",     label: "Gifts",      accent: "#E91E63", roots: giftsCategories,    subs: giftsSubcategories,    products: giftsProducts },
  { slug: "fashion",   label: "Fashion",    accent: HEART.black, roots: FASHION_ADULT_CATEGORIES, subs: FASHION_ADULT_SUBCATEGORIES, products: FASHION_ADULT_PRODUCTS },
  { slug: "beauty",    label: "Beauty",     accent: "#C9A84C", roots: BEAUTY_ADULT_CATEGORIES, subs: BEAUTY_ADULT_SUBCATEGORIES, products: AllBeautyProducts },

  // 🍼 Maternity — full Baby & Maternity tree, no stripping
  {
    slug: "maternity",
    label: "Maternity",
    accent: "#F48FB1",
    roots: BabyMaternityCategories,
    subs: [
      ...BabyClothing, ...BabyShoes, ...BabyCare, ...MommyCare, ...BabyFeeding,
      ...BabyBedtime, ...BabyTravel, ...BabyToys, ...BabyNursery, ...Maternity,
      ...BabyHealthSafety, ...ActivityEntertainment, ...BabyAccessories,
      ...PottyTraining, ...MaternityGifts, ...BabyStationery,
      ...CarSeatsAccessories, ...MaternityClothing, ...BabyTeething,
    ],
    products: AllBabyMaternityProducts,
  },

  // 🛋️ Furniture — matches idFurnitureCategories import shape
  { slug: "furniture",  label: "Furniture",    accent: "#6D4C41", roots: idFurnitureCategories,     subs: idFurnitureSubcategories,     products: idFurnitureProducts },

  // ── flip any of these on by deleting the leading "// " ──
  // { slug: "fastfood",  label: "Fast Food",  accent: "#FF6A00", roots: fastfoodCategories, subs: fastfoodSubcategories, products: fastfoodProducts },
  // { slug: "farm",      label: "Farm & Fresh", accent: "#43A047", roots: farmCategories,   subs: farmSubcategories,     products: farmProducts },
  // { slug: "electronics", label: "Electronics",  accent: "#1565C0", roots: electronicsCategories,     subs: electronicsSubcategories,     products: electronicsProducts },
  // { slug: "toys",        label: "Toys",          accent: "#FFC107", roots: idToysCategories,          subs: idToysSubcategories,          products: idToysProducts },
  // { slug: "gaming",      label: "Gaming",        accent: "#6A1B9A", roots: idGamingCategories,        subs: idGamingSubcategories,        products: idGamingProducts },
  // { slug: "sports",      label: "Sports",        accent: "#388E3C", roots: sportsCategories,          subs: sportsSubcategories,          products: sportsProducts },
  // { slug: "artcrafts",   label: "Art & Crafts",  accent: "#8E44AD", roots: idArtCraftsCategories,     subs: idArtCraftsSubcategories,     products: idArtCraftsProducts },
  // { slug: "aviation",    label: "Aviation",      accent: "#0277BD", roots: idAviationCategories,      subs: idAviationSubcategories,      products: idAviationProducts },
  // { slug: "automotive",  label: "Automotive",    accent: "#455A64", roots: automotiveCategories,      subs: automotiveSubcategories,      products: automotiveProducts },
  // { slug: "tools",       label: "Tools",         accent: "#607D8B", roots: toolsCategories,           subs: toolsSubcategories,           products: toolsProducts },
  // { slug: "pets",        label: "Pets",          accent: "#8D6E63", roots: idPetsCategories,          subs: idPetsSubcategories,          products: idPetsProducts },
   { slug: "kitchen",     label: "Kitchen",       accent: "#D84315", roots: kitchenCategories,         subs: kitchenSubcategories,         products: kitchenProducts },
  // { slug: "luggage",     label: "Luggage",       accent: "#37474F", roots: idLuggageCategories,       subs: idLuggageSubcategories,       products: idLuggageProducts },
  // { slug: "books",       label: "Books",         accent: "#3F51B5", roots: idBooksCategories,         subs: idBooksSubcategories,         products: idBooksProducts },
];

/* flat id -> {name, vertical} lookup, built from whatever is active */
const CATEGORY_LOOKUP: Record<number, { name: string; verticalLabel: string }> = {};
VERTICALS.forEach(v => {
  [...v.roots, ...v.subs, ...v.products].forEach((item: any) => {
    CATEGORY_LOOKUP[item.id] = { name: item.name, verticalLabel: v.label };
  });
});

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";

const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};

/* tier 0 = same city, 1 = same country, 2 = same continent, 3 = rest of world
   LOCAL → tier 0-1, GLOBAL → tier 2-3. Disjoint sets — same split used by
   GroceriesHome, so toggling Global never mixes in a local-tier brand
   and vice versa, and each mode gets its own honest empty state. */
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&family=Archivo+Black&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button { font-family: inherit; }
input::placeholder { color: rgba(27,14,6,.35); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes mc-ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
@keyframes mc-pulse   { 0%,100%{opacity:1} 50%{opacity:.3} }
@keyframes titleGlow  { 0%,100%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 14px 30px rgba(200,32,46,.18);} 50%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 18px 40px rgba(200,32,46,.30);} }
@keyframes pv-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }

.p-card { transition: box-shadow .22s, transform .22s; }
.p-card:hover { box-shadow: 0 4px 10px rgba(0,0,0,.06), 0 24px 48px rgba(0,0,0,.16); transform: translateY(-6px); }

.add-btn-3d { transition: transform .18s ease, box-shadow .18s ease; }
.add-btn-3d:hover  { transform: translateY(-3px); box-shadow: 0 10px 24px var(--shadow) !important; }
.add-btn-3d:active { transform: translateY(0) scale(.96); box-shadow: 0 4px 14px var(--shadow) !important; }

.nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer; font-size:14px; font-weight:700; color:${T.offwhite}; background:none; width:100%; text-align:left; border:none; border-bottom:1px solid ${HEART.divider}; transition:background .15s; }
.nav-item:hover { background: ${HEART.goldSoft}; }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:${T.mutedDim}; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:${T.red}; }

.pv-title {
  font-family: 'Archivo Black','Montserrat',sans-serif;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 0.95;
  background: linear-gradient(180deg, ${HEART.dark} 0%, ${HEART.dark} 60%, ${HEART.red} 130%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: titleGlow 3.4s ease-in-out infinite;
}

.pv-pill { transition: transform .15s ease, box-shadow .15s ease, background .15s ease, color .15s ease; }
.pv-pill:hover { transform: translateY(-2px); }

.pv-hbtn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.16);
         border:1px solid rgba(255,255,255,.3);cursor:pointer;color:#fff;
         font-size:12px;font-weight:700;padding:9px 14px;border-radius:999px;
         flex-shrink:0;white-space:nowrap;transition:background .2s;font-family:inherit;}
.pv-hbtn:hover{background:rgba(255,255,255,.26);}

.pv-scg-tile{background:none;border:none;cursor:pointer;padding:0;border-radius:14px;overflow:hidden;}
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR STYLES
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard:   { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(27,14,6,.06)", border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  category:       { padding: 10, borderRadius: 12, background: HEART.bg, marginTop: 8, border: `1px solid ${HEART.border}`, cursor: "pointer" },
  row:            { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub:            { paddingLeft: 22, paddingTop: 8 },
  productGroup:   { paddingLeft: 20, paddingTop: 7, paddingBottom: 4, fontWeight: 600, color: T.mutedDim, cursor: "pointer", fontSize: 13 },
  sidebar:        { background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20, boxShadow: "0 4px 24px rgba(27,14,6,.08)", position: "sticky" as const, top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const, border: T.outline },
};

const S: Record<string, React.CSSProperties> = {
  page:        { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Montserrat',sans-serif", position: "relative" as const },
  content:     { position: "relative" as const, zIndex: 1 },
  headerTop:   { background: GRAD_H, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_RED(0.25)}` },
  searchWrap:  { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: `1.5px solid rgba(255,255,255,.32)`, transition: "border-color .2s" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn:   { width: 42, minHeight: 40, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  hBtn:        { display: "flex", alignItems: "center", gap: 6, background: HEART.goldSoft, border: `1px solid ${HEART.border}`, cursor: "pointer", color: HEART.dark, fontSize: 12, fontWeight: 700, padding: "7px 13px", borderRadius: 999, flexShrink: 0, whiteSpace: "nowrap" as const, transition: "background .2s" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid:        { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 },
  mobileGrid:  { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs:  { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: T.cardBg, display: "flex", justifyContent: "space-around", alignItems: "center", borderTop: `1px solid ${HEART.border}`, zIndex: 100 },
};

/* ═══════════════════════════════════════════════════════════
   PAGE WATERMARK
═══════════════════════════════════════════════════════════ */
const PageWatermark: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
      backgroundImage: `url(${appLogo})`, backgroundRepeat: "repeat",
      backgroundSize: "170px 170px", opacity: 0.045, filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const ProductsHome: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId     = authUser?.userId ?? "";

  const notifications = useSelector((s: RootState) => s.notifications.notifications);
  const filteredListings = useSelector((s: RootState) => s.products.filteredProducts);
  const cartItems        = useSelector((s: RootState) => s.cart.items);
  const ratingSummaries   = useSelector((s: RootState) => s.productRatings.summaries);

  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);
  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile,        setIsMobile]        = useState(window.innerWidth <= 768);
  const [catOpen,         setCatOpen]         = useState(false);
  const [desktopCatOpen,  setDesktopCatOpen]  = useState(false);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchFocused,   setSearchFocused]   = useState(false);
  const [activeSort,      setActiveSort]      = useState<SortKey>("default");

  /* ── active vertical (the toggle) ── */
  const [activeVertical, setActiveVertical] = useState<string>(VERTICALS[0]?.slug ?? "");
  const vertical = useMemo(() => VERTICALS.find(v => v.slug === activeVertical) ?? VERTICALS[0], [activeVertical]);

  const [activeCat,           setActiveCat]           = useState<number | null>(null);
  const [activeSub,           setActiveSub]           = useState<number | null>(null);
  const [activeProductGroup,  setActiveProductGroup]  = useState<number | null>(null);

  const [openCategory,    setOpenCategory]    = useState<string | null>(VERTICALS[0]?.slug ?? null);
  const [openSubCategory, setOpenSubCategory] = useState<number | null>(null);
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  const [userCountry,       setUserCountry]       = useState<string | null>(null);
  const [userCity,          setUserCity]          = useState<string | null>(null);
  const [userLat,           setUserLat]           = useState<number | null>(null);
  const [locationDenied,    setLocationDenied]    = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal,        setShowGlobal]        = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [selColor, setSelColor] = useState<number | null>(null);
  const [selSize,  setSelSize]  = useState<number | null>(null);
  const [qty,      setQty]      = useState(1);
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantItem, setVariantItem] = useState<any | null>(null);

  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + i.qty, 0);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries()); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners()); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null)); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null));
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get   = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalProductsByCountry(country));
              }
            });
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null));
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then(r => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  }, [userCountry, showGlobal, dispatch]);

  // 🔍 DEBUG EFFECT
 
  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveCat(null); setActiveSub(null); setActiveProductGroup(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry));
  };

  useEffect(() => { if (searchQuery.trim()) setActiveProductGroup(null); }, [searchQuery]);

  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveSub(null); setActiveProductGroup(null); setActiveSort("default");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry));
  };

  /* switching vertical clears any active category filter from the previous
     one, and leaves the new vertical's category group expanded (not
     collapsed) so its categories are visible immediately. */
  const handleVerticalSelect = (slug: string) => {
    setActiveVertical(slug);
    setActiveCat(null); setActiveSub(null); setActiveProductGroup(null);
    setOpenCategory(slug); setOpenSubCategory(null);
  };

  /* category select (toggle) — FILTERS the list below, never navigates
     (same pattern as ElectronicsHome's handleCatSelect) */
  const handleCatSelect = (catId: number) => {
    const isAlreadyActive = activeCat === catId;
    setActiveCat(isAlreadyActive ? null : catId); setActiveSub(null); setActiveProductGroup(null);
  };

  const handleSubSelect = (subId: number) => { setActiveSub(subId); };

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  
  

  const getVariantMode = (item: any): "color" | null => {
    if (!item || !item.variants || !Object.keys(item.variants).length) return null;
    return "color";
  };

  const getFlatStock = (item: any) => item?.stock ?? 0;
  const getVariantStock = (item: any, colorId: number | null) =>
    colorId !== null ? (item?.variants?.[colorId]?.stock ?? 0) : 0;
  const isOutOfStock = (item: any) => {
    if (item?.available === false) return true;
    if (getVariantMode(item) === null) return getFlatStock(item) <= 0;
    const variants = item?.variants ?? {};
    const ids = Object.keys(variants);
    if (ids.length === 0) return true;
    return ids.every((k) => (variants[k]?.stock ?? 0) <= 0);
  };

  /* size options available for the currently selected colour — used
     both to render the size row and to decide whether a size pick is
     required before the shopper can continue. */
  const variantSizeIdsForSelColor = useMemo(() => {
    if (!variantItem || selColor === null) return [];
    return variantItem.variants?.[selColor]?.sizeIds ?? [];
  }, [variantItem, selColor]);

  const flatStockForModal = variantItem?.stock ?? 0;
  const selectedVariantStock = selColor !== null ? getVariantStock(variantItem, selColor) : 0;
  const relevantStock = getVariantMode(variantItem) === null ? flatStockForModal : selectedVariantStock;

  const canContinue = getVariantMode(variantItem) === null
    ? qty > 0 && flatStockForModal > 0 && qty <= flatStockForModal
    : (selColor !== null && qty > 0
        && (variantSizeIdsForSelColor.length === 0 || selSize !== null)
        && selectedVariantStock > 0
        && qty <= selectedVariantStock);

  /* opens the product detail sheet/modal. Used both as the primary
     click target on every product card AND — for products that need
     a colour/size pick — as the target of the quick "add to bag"
     button. Nothing here navigates away from the page. */
  const openProductModal = (item: any) => {
    setVariantItem(item); setSelColor(null); setSelSize(null); setQty(1); setVariantOpen(true);
  };

  const closeProductModal = () => {
    setVariantOpen(false); setVariantItem(null); setSelColor(null); setSelSize(null);
  };

  const handleAddToCart = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (getVariantMode(item) !== null) { openProductModal(item); return; }
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleVariantContinue = (toCheckout: boolean) => {
    if (!variantItem) return;
    ensureCartBrandCompat(variantItem.brandId);
    dispatch(addToCart({
      listingId: variantItem.listingId, name: variantItem.name!,
      rate: variantItem.rate!, qty,
      photo: variantItem.variants?.[selColor!]?.imageUrl ?? variantItem.photos?.[0],
      colorId: selColor ?? undefined, sizeId: selSize ?? undefined,
      hostId: variantItem.hostId, brandId: variantItem.brandId,
    } as any));
    closeProductModal(); setQty(1);
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const goToCheckout = () => navigate(`/productcheckout/${userId}`, { state: { items: cartItems } });

  /* ── filtered products: category / sub / product-group / search +
     optional sort. Location/tier filtering happens AFTER this, at the
     brand-group level below — kept separate so it's never silently
     skipped for any combination of sort/filter. ── */
  /* every id (root category, subcategory, product group) that belongs
     to the currently active vertical — this is what actually scopes
     the product grid to "Grocery" vs "Furniture" vs "Fashion" etc.
     Without this, switching the vertical toggle only swapped the
     sidebar tree/icons while the grid kept showing everything. */
  const verticalIdSet = useMemo(() => {
    if (!vertical) return new Set<number>();
    const ids = new Set<number>();
    [...vertical.roots, ...vertical.subs, ...vertical.products].forEach((c: any) => ids.add(c.id));
    return ids;
  }, [vertical]);

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = filteredListings.filter((l: any) => {
      if (!l.isPublished) return false;
      const matchesVertical =
        verticalIdSet.has(l.categoryId) || verticalIdSet.has(l.subcategoryId) || verticalIdSet.has(l.productId);
      if (!matchesVertical) return false;
      if (activeCat) {
        const matchesCat = l.categoryId === activeCat || l.subcategoryId === activeCat || l.productId === activeCat;
        if (!matchesCat) return false;
      }
      if (activeSub) {
        const matchesSub = l.subcategoryId === activeSub || l.productId === activeSub;
        if (!matchesSub) return false;
      }
      if (activeProductGroup && l.productId !== activeProductGroup && l.productVariantId !== activeProductGroup) return false;
      if (q && !`${l.name ?? ""} ${l.description ?? ""}`.toLowerCase().includes(q)) return false;
      return true;
    });

    if (activeSort === "price_asc")  list = [...list].sort((a: any, b: any) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc")   list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest")     list = [...list].sort((a: any, b: any) => (b.createdAt ?? 0) - (a.createdAt ?? 0));

    return list;
  }, [filteredListings, verticalIdSet, activeCat, activeSub, activeProductGroup, searchQuery, activeSort]);

  /* ── group by brand, then apply the SAME disjoint LOCAL/GLOBAL tier
     split used by GroceriesHome:
       LOCAL  (tier 0-1, city/country) — shown when showGlobal is false
       GLOBAL (tier 2-3, continent+)   — shown when showGlobal is true
     The two sets never overlap, so toggling Global never repeats a
     brand the other mode already showed, and each mode gets its own
     honest "nothing found" state instead of silently borrowing from
     the other set. This mirrors GroceriesHome's productsByBrand. ── */
  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map(bId => ({ brandId: bId, items: map.get(bId)! }));

    if (locationActive) {
      groups = groups.filter(g => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    groups = [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
    return groups;
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleProductCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeCatName = activeCat ? CATEGORY_LOOKUP[activeCat]?.name ?? null : null;



 
  /* ═══════════════════════════════════
     HEADER — same anatomy as ElectronicsHome's HeaderBar:
     location pill · search · categories (grid icon) · cart
  ═══════════════════════════════════ */
  const HeaderBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      {locationDenied ? (
        <button className="pv-hbtn" onClick={() => setLocationModalOpen(true)}><AlertTriangle size={14} /></button>
      ) : !userCountry ? (
        <div className="pv-hbtn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "pv-pulse 1.2s infinite" }} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Locating…</span>
        </div>
      ) : (
        <button onClick={handleToggleGlobal} className="pv-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={S.searchWrap}>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder="Search Heartlas…"
          style={S.searchInput}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button style={S.searchBtn}><Search size={16} color="#fff" /></button>
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen(v => !v))} className="pv-hbtn">
        <LayoutGrid size={14} />
        <span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={goToCheckout} className="pv-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Cart"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: HEART.redDark, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>
    </div>
  );

  /* ═══════════════════════════════════
     🎬 MOVIE-TITLE HERO — the currently
     selected vertical, huge & bold
  ═══════════════════════════════════ */
  const VerticalTitleBanner = () => (
    <div style={{ padding: isMobile ? "6px 4px 4px" : "4px 4px 8px", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
      <span className="pv-title" style={{ fontSize: isMobile ? 30 : 46 }}>
        {vertical?.label ?? "Shop"}
      </span>
      <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim, textTransform: "uppercase" }}>
        {locationDenied ? "Worldwide" : showGlobal ? "Global" : userCity ?? "Near You"}
      </span>
    </div>
  );

  /* ═══════════════════════════════════
     VERTICAL TOGGLE BAR — the master switch
  ═══════════════════════════════════ */
  const VerticalToggleBar = () => (
    <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, padding: isMobile ? "0 4px 12px" : "0 4px 16px" }}>
      {VERTICALS.map(v => {
        const isActive = v.slug === activeVertical;
        return (
          <button
            key={v.slug}
            className="pv-pill"
            onClick={() => handleVerticalSelect(v.slug)}
            style={{
              flexShrink: 0, border: "none", cursor: "pointer", borderRadius: 999,
              padding: isMobile ? "8px 16px" : "10px 20px",
              fontSize: isMobile ? 12 : 13, fontWeight: 800, letterSpacing: "0.03em",
              background: isActive ? `linear-gradient(135deg, ${v.accent}, ${v.accent}CC)` : "#fff",
              color: isActive ? "#fff" : T.black,
            
              boxShadow: isActive ? `0 8px 20px ${v.accent}55` : "0 2px 8px rgba(27,14,6,.06)",
              outline: isActive ? "none" : `1.5px solid ${HEART.border}`,
            }}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     SIDEBAR TREE — loops ONLY over the active vertical
     (switch verticals via the toggle bar / pills above)
  ═══════════════════════════════════════════════════════════ */
  const VerticalCategoryTree: React.FC<{ v: Vertical }> = ({ v }) => {
    const isOpen = openCategory === v.slug;

    return (
      <div style={A.commerceCard}>
        <div style={A.commerceHeader} onClick={() => setOpenCategory(isOpen ? null : v.slug)}>
          {v.label}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
              {v.roots.map((cat: any) => {
                const isCatOpen = openSubCategory === cat.id;
                const subsForCat = v.subs.filter((s: any) => s.parentId === cat.id);

                return (
                  <div key={cat.id} style={A.category}>
                    <div style={A.row} onClick={() => { const next = isCatOpen ? null : cat.id; setOpenSubCategory(next); if (activeCat !== cat.id) handleCatSelect(cat.id); }}>
                      {renderCatIcon(cat, 15, v.accent)}
                      {cat.name}
                      <motion.div animate={{ rotate: isCatOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}><ChevronRight size={14} color={T.mutedDim} /></motion.div>
                    </div>
                    <AnimatePresence>
                      {isCatOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                          <div style={A.sub}>
                            {subsForCat.map((sub: any) => {
                              const isSubOpen = openSubCategory === sub.id + 100000000;
                              return (
                                <div key={sub.id}>
                                  <div style={A.row} onClick={() => { setOpenSubCategory(isSubOpen ? cat.id : sub.id + 100000000); handleSubSelect(sub.id); }}>
                                    {renderCatIcon(sub, 13, v.accent)}
                                    {sub.name}
                                    <motion.div animate={{ rotate: isSubOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}><ChevronRight size={13} color={T.mutedDim} /></motion.div>
                                  </div>
                                  <AnimatePresence>
                                    {isSubOpen && (
                                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                                        {v.products.filter((p: any) => p.parentId === sub.id).map((p: any) => (
                                          <div key={p.id}
                                            style={{ ...A.productGroup, color: activeProductGroup === p.id ? T.red : T.mutedDim, fontWeight: activeProductGroup === p.id ? 800 : 600, display: "flex", alignItems: "center", gap: 6 }}
                                            onClick={() => { setActiveProductGroup(p.id); closePanel(); }}>
                                            {renderCatIcon(p, 12, v.accent)}
                                            {p.name}
                                          </div>
                                        ))}
                                        {v.products.filter((p: any) => p.parentId === sub.id).length === 0 && (
                                          <div style={{ ...A.productGroup, color: HEART.textFaint, cursor: "default" }}>No items yet</div>
                                        )}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const CategoryPanelContent = () => (
    <>
      <VerticalToggleBar />
      {vertical && <VerticalCategoryTree v={vertical} />}
    </>
  );

  const MobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: HEART.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${HEART.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.red }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={15} color={T.muted} /></button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}><CategoryPanelContent /></div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     CATEGORY ICON BAR — active vertical's roots
  ═══════════════════════════════════ */
  const CategoryIconBar = () => (
    <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: HEART.panelBg, borderRadius: 18, border: T.outline }}>
      <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
        {vertical?.roots.map((cat: any) => {
          const isSel = activeCat === cat.id;
          const color = cat.color ?? vertical.accent;
          return (
            <button key={cat.id}
              onClick={() => handleCatSelect(cat.id)}
              style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
              <div style={{
                width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%", overflow: "hidden",
                border: `2px solid ${isSel ? T.red : HEART.border}`,
                boxShadow: isSel ? `0 0 0 3px ${T.redDim}` : "none", transition: "all .2s",
              }}>
              {cat.image ? (
  <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85, transition: "opacity .2s" }} />
) : (
  <div style={{ width: "100%", height: "100%", background: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <span style={{ color: "#fff", display: "flex" }}>{renderCatIcon(cat, isMobile ? 20 : 24, "#fff")}</span>
  </div>
)}
              </div>
              <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: isSel ? T.red : T.black, whiteSpace: "nowrap" }}>{cat.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — active vertical's roots, chunked 4-up.
     Tiles SELECT a filter (handleCatSelect), never navigate —
     same pattern as ElectronicsHome's ShopByCategory.
  ═══════════════════════════════════ */
  const ShopByCategory = () => {
    if (!vertical) return null;
    const chunk = (arr: any[], title: string) => {
      const chunks: any[][] = [];
      for (let i = 0; i < arr.length; i += 4) chunks.push(arr.slice(i, i + 4));
      return chunks.map((items, idx) => ({ title: chunks.length > 1 ? `${title} ${idx + 1}` : title, items }));
    };
    const cardGroups = chunk(vertical.roots, `${vertical.label} Edit`);

    return (
      <div style={{ marginBottom: 28 }}>
        <div onClick={() => setShopCatExpanded(v => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>Shop by Category</span>
          <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}><ChevronDown size={20} color={T.muted} /></motion.div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div key={vertical.slug} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group) => (
                  <div key={group.title}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 20, overflow: "hidden", border: T.outline, boxShadow: "0 6px 28px rgba(27,14,6,.06)", transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${T.redDim}, 0 16px 38px rgba(27,14,6,.16)`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = "0 6px 28px rgba(27,14,6,.06)"; el.style.borderColor = HEART.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -.2 }}>{group.title}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.items.map((cat: any) => {
                        const sel = activeCat === cat.id;
                        return (
                        <button key={cat.id} className="pv-scg-tile" onClick={() => handleCatSelect(cat.id)} style={{ padding: 0 }}>
                          <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: HEART.bg, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2, transition: "outline .15s" }}>
                            {cat.image ? (
                              <>
                                <img src={cat.image} alt={cat.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${HEART.dark}EE, transparent 50%)` }} />
                                <span style={{ position: "absolute", bottom: isMobile ? 4 : 6, left: 0, right: 0, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                                  {cat.name}
                                </span>
                                {sel && (
                                  <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Check size={11} color="#fff" strokeWidth={3} />
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, position: "relative" }}>
                                <span style={{ fontSize: 26, color: cat.color ?? vertical.accent, display: "flex" }}>{renderCatIcon(cat, 26)}</span>
                                <span style={{ fontSize: isMobile ? 9 : 11, fontWeight: 800, color: T.black, textAlign: "center", padding: "0 4px" }}>{cat.name}</span>
                                {sel && (
                                  <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Check size={11} color="#fff" strokeWidth={3} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                        );
                      })}
                    </div>
                    <button onClick={() => handleCatSelect(group.items[0].id)}
                      style={{ width: "100%", padding: isMobile ? "7px 12px" : "9px 16px", background: "none", border: "none", borderTop: `1.5px solid ${HEART.divider}`, cursor: "pointer", fontSize: isMobile ? 11 : 13, fontWeight: 700, color: T.red, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      See all<ArrowRight size={isMobile ? 12 : 14} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BRAND ROW HEADER
  ═══════════════════════════════════ */
  const BrandRowHeader = ({ brandId }: { brandId: string }) => {
    const partner  = brandMap.get(brandId);
    const shopName = partner?.name || "Boutique";
    const shopLogo = partner?.image || appLogo;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`, background: "#fff" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </div>
        <span style={{ fontWeight: 900, fontSize: 14, color: T.black, textDecoration: "underline", textDecorationColor: T.red, textDecorationThickness: 2, textUnderlineOffset: 4 }}>{shopName}</span>
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => navigate(`/StoreHome/${brandId}`)}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff", boxShadow: "0 3px 10px rgba(27,14,6,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={T.red} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT CARD — click opens the in-page detail
     sheet/modal (openProductModal). Nothing here navigates away.
  ═══════════════════════════════════ */
  const ProductCard = ({ item, isMob }: { item: any; isMob: boolean }) => {
    const q         = getQty(item.listingId);
    const outOfStock = isOutOfStock(item);
    const price     = `${symbol ?? "€"}${((item.rate ?? 0) * (rate ?? 1)).toFixed(2)}`;
    const discount  = item.discount ?? (item.compareAtRate && item.compareAtRate > (item.rate ?? 0)
      ? Math.round(((item.compareAtRate - (item.rate ?? 0)) / item.compareAtRate) * 100) : null);
    const summary   = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const accent    = productAccentColor(item.listingId ?? item.name ?? "x");
    const isWished  = wished.has(item.listingId);

    const overlap  = isMob ? 58 : 100;
    const imgZoneH = isMob ? 200 : 300;

    return (
      <motion.div className="p-card" whileTap={{ scale: 0.96 }}
        style={{ borderRadius: 20, overflow: "visible", background: "#fff", boxShadow: "0 1px 2px rgba(27,14,6,.05), 0 10px 28px rgba(27,14,6,.10)", cursor: "pointer", display: "flex", flexDirection: "column", height: "100%", marginTop: overlap }}
        onClick={() => openProductModal(item)}>
        <div style={{ width: "100%", height: imgZoneH, marginTop: -overlap, position: "relative", zIndex: 4, overflow: "visible", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {item.photos?.[0] ? (
            <div style={{ width: "115%", height: "115%", transform: "translateY(-5%)" }}>
              <motion.img src={item.photos[0]} alt={item.name} whileHover={{ scale: 1.06 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom", display: "block", filter: "drop-shadow(0 16px 20px rgba(27,14,6,.25))" }} />
            </div>
          ) : (
            <div style={{ width: "60%", height: "70%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Package size={isMob ? 40 : 50} color={T.mutedDim} style={{ opacity: 0.35 }} />
            </div>
          )}

          <button onClick={e => toggleWish(item.listingId, e)}
            style={{ position: "absolute", top: overlap - 4, right: 4, background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.12)", zIndex: 6 }}>
            <Heart size={13} color={isWished ? T.red : T.muted} fill={isWished ? T.red : "none"} />
          </button>

          {discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 999, zIndex: 5 }}>{discount}% OFF</div>
          )}
          {item.isNewArrival && !discount && (
            <div style={{ position: "absolute", top: overlap + 8, left: 4, background: T.black, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 999, zIndex: 5 }}>NEW</div>
          )}
          {hasRating && (
            <div style={{ position: "absolute", top: overlap + (discount || item.isNewArrival ? 30 : 8), left: 4, zIndex: 6, background: "rgba(255,255,255,.92)", border: `1px solid ${T.border}`, borderRadius: 999, padding: isMob ? "3px 7px" : "4px 9px", display: "flex", alignItems: "center", gap: 3 }}>
              <Star size={isMob ? 9 : 10} fill={T.black} color={T.black} />
              <span style={{ color: T.black, fontWeight: 800, fontSize: isMob ? 9 : 10 }}>{summary!.avgRating.toFixed(1)}</span>
              <span style={{ color: T.muted, fontSize: isMob ? 8 : 9 }}>({summary!.totalReviews.toLocaleString()})</span>
            </div>
          )}
        </div>

        <div style={{ padding: isMob ? "2px 12px 12px" : "0px 16px 16px", display: "flex", flexDirection: "column", gap: isMob ? 2 : 3, flex: 1, justifyContent: "flex-end" }}>
          <div style={{ fontWeight: 800, fontSize: isMob ? 13 : 15, color: T.black, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: isMob ? 19 : 24 }}>
            {item.name}
          </div>
          <div style={{ fontWeight: 900, fontSize: isMob ? 16 : 19, color: T.black }}>{price}</div>

          {outOfStock ? (
            <div style={{
              width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", borderRadius: 999, marginTop: isMob ? 2 : 4,
              border: `2px solid ${T.mutedDim}`, display: "flex", alignItems: "center", justifyContent: "center",
              background: HEART.bg, color: T.mutedDim, fontWeight: 800, fontSize: isMob ? 11 : 13, letterSpacing: "0.06em",
            }}>
              OUT OF STOCK
            </div>
          ) : q > 0 ? (
            <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={e => e.stopPropagation()} className="add-btn-3d"
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: `2px solid ${accent}`, borderRadius: 999, padding: "0 6px", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, marginTop: isMob ? 2 : 4 }}>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={e => { e.stopPropagation(); dispatch(decrementCartItem({ listingId: item.listingId })); }}>
                <Minus size={isMob ? 12 : 14} color="#fff" />
              </button>
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 13 : 15 }}>{q}</span>
              <button style={{ width: isMob ? 26 : 30, height: isMob ? 26 : 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
                <Plus size={isMob ? 12 : 14} color="#fff" />
              </button>
            </motion.div>
          ) : (
            <motion.button className="add-btn-3d" whileTap={{ scale: 0.96 }}
              style={{ width: "100%", height: isMob ? 40 : 46, boxSizing: "border-box", borderRadius: 999, marginTop: isMob ? 2 : 4, border: `2px solid ${accent}`, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, background: "#fff", ["--shadow" as any]: `${accent}45`, boxShadow: `0 4px 14px var(--shadow)`, cursor: "pointer" }}
              onClick={e => { e.stopPropagation(); handleAddToCart(item); }}>
              <ShoppingCart size={isMob ? 13 : 15} color={accent} />
              <span style={{ color: accent, fontWeight: 900, fontSize: isMob ? 11 : 13, letterSpacing: "0.06em" }}>ADD TO BAG</span>
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  };

  const SorryNoStores = ({ categoryName }: { categoryName: string }) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>Sorry, no {categoryName} stores found</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Try another category, or switch to a different vertical above.</p>
      <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All Categories</button>
    </div>
  );

  const NoVerticalBrands = () => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>No {vertical?.label ?? "matching"} brands found</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Nobody's selling in this category yet — try switching category, or try another vertical above.</p>
      <button onClick={resetAllFilters} style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>View All Categories</button>
    </div>
  );

  /* ── products grid, grouped by store, with its own honest empty
     state for each distinct reason the grid can be empty:
       1. the active VERTICAL/category has no matching brands at all
          (independent of location) — "No {vertical} brands found"
       2. location hasn't been resolved yet
       3. the vertical/category HAS brands, but the local/global tier
          filter is what emptied the list — mirrors GroceriesHome's
          disjoint "no local products" / "no global products" states
  ── */
  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeCatName ?? (locationDenied ? "All Products" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All Products")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: HEART.goldSoft, padding: "2px 9px", borderRadius: 999 }}>{visibleProductCount}</span>
        </div>
        {locationActive && (
          <button onClick={handleToggleGlobal} style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? HEART.bg : T.redDim, borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? HEART.border : "rgba(200,32,46,.3)"}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.red, cursor: "pointer" }}>
            {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show Local" : "Show Global"}
          </button>
        )}
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0 ? (
          /* nothing matches this vertical/category combo at all — has
             nothing to do with location, so don't blame Local/Global */
          activeCatName ? <SorryNoStores categoryName={activeCatName} /> : <NoVerticalBrands />
        ) : locationDenied ? (
          <div style={{ textAlign: "center", padding: 56 }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
            <h3 style={{ fontWeight: 900, color: T.black, marginBottom: 8 }}>Location needed</h3>
            <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>Enable location to see local products.</p>
            <button onClick={() => setLocationModalOpen(true)}
              style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
              Enable Location
            </button>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 56 }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>✦</div>
            <h3 style={{ fontWeight: 900, color: T.black, marginBottom: 8 }}>
              {showGlobal ? `No global ${vertical?.label ?? "products"} found.` : `No local ${vertical?.label ?? "products"} found.`}
            </h3>
            <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>
              Try switching to {showGlobal ? "Local" : "Global"} above, or clear your filters.
            </p>
            <button onClick={resetAllFilters}
              style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
              View All Products
            </button>
          </div>
        )
      ) : (
        productsByBrand.map(group => (
          <div key={group.brandId}>
            <BrandRowHeader brandId={group.brandId} />
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => <ProductCard key={item.listingId} item={item} isMob={isMob} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     PRODUCT DETAIL — bottom sheet (mobile) / modal (desktop).
     This is the ONLY way a product's details are shown; clicking a
     product card never navigates away from the page. Shows the
     store/brand, name, price, colour + size attributes (when the
     product has variants), description, quantity, and Add to Bag /
     Buy Now — the same pattern GroceriesHome uses for its detail
     sheet, adapted for Fashion/Beauty/Maternity/Furniture items.
  ═══════════════════════════════════ */
  const VariantModal = () => {
    if (!variantOpen || !variantItem) return null;
    const variantColorIds = Object.keys(variantItem?.variants ?? {}).map(Number);
    const sizeIds: number[] = variantSizeIdsForSelColor;
    const previewImg = selColor && variantItem?.variants?.[selColor]?.imageUrl ? variantItem.variants[selColor].imageUrl : variantItem?.photos?.[0];
    const brandName = brandMap.get(variantItem?.brandId)?.name;

    const CloseBtn = () => (
      <button onClick={closeProductModal}
        style={{ background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.18)" }}>
        <X size={15} color={T.black} />
      </button>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 460, width: "100%", maxHeight: isMobile ? "85vh" : "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: HEART.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "20px 20px 0" }}>
          {previewImg ? (
            <div style={{ position: "relative", width: "100%", aspectRatio: "16/7", borderRadius: 14, overflow: "hidden", marginBottom: 16, background: HEART.bg }}>
              <img src={previewImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", top: 10, right: 10 }}><CloseBtn /></div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}><CloseBtn /></div>
          )}

          {brandName && (
            <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: T.red, textTransform: "uppercase", marginBottom: 4 }}>{brandName}</p>
          )}
          <div style={{ fontSize: 15, fontWeight: 800, color: T.black, marginBottom: 4 }}>{variantItem?.name}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: T.black, marginBottom: 18 }}>{symbol ?? "€"}{((variantItem?.rate ?? 0) * (rate ?? 1)).toFixed(2)}</div>

          {variantColorIds.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 12 }}>
                COLOUR
                {selColor && (() => {
                  const cv = colorPalette.find(c => c.id === selColor);
                  return cv ? <span style={{ fontWeight: 600, color: T.red, letterSpacing: "0.06em", textTransform: "none", fontSize: 12 }}> — {cv.name}</span> : null;
                })()}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(64px,1fr))", gap: 10, marginBottom: 20 }}>
                {variantColorIds.map(cId => {
                  const cv = colorPalette.find(c => c.id === cId);
                  const hex = (cv as any)?.hex ?? (cv as any)?.hexCodes?.[0];
                  const isSel = selColor === cId;
                  const stockForColor = getVariantStock(variantItem, cId);
                  const colorDisabled = stockForColor <= 0;
                  return (
                    <button key={cId} disabled={colorDisabled}
                      onClick={() => { if (colorDisabled) return; setSelColor(cId); setSelSize(null); }}
                      style={{ background: "none", border: "none", padding: 0, cursor: colorDisabled ? "not-allowed" : "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, opacity: colorDisabled ? 0.35 : 1, filter: colorDisabled ? "grayscale(1)" : "none" }}>
                      <div style={{ position: "relative", width: 40, height: 40 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: "50%",
                          background: hex ? sphere3D(hex) : sphere3D(HEART.dark),
                          boxShadow: `inset -6px -5px 12px rgba(27,14,6,.30),inset 5px 5px 10px rgba(255,255,255,.18),0 5px 18px rgba(27,14,6,.20)${isSel ? ",0 0 0 2.5px " + T.red : ""}`,
                          outline: isSel ? `2.5px solid ${T.red}` : "none", outlineOffset: 3,
                        }} />
                        {isSel && (
                          <div style={{ position: "absolute", bottom: -3, right: -3, width: 16, height: 16, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(27,14,6,.3)" }}>
                            <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 9, fontWeight: isSel ? 700 : 500, color: isSel ? T.red : T.mutedDim, textAlign: "center", maxWidth: 60, wordBreak: "break-word" as const, lineHeight: 1.2 }}>{cv?.name ?? String(cId)}{colorDisabled ? " · Sold out" : ""}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {selColor && sizeIds.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 12 }}>SIZE</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                {sizeIds.map((sId: number) => {
                  const sz = sizePalette.find(s => s.id === sId);
                  const isSel = selSize === sId;
                  return (
                    <button key={sId} onClick={() => setSelSize(sId)}
                      style={{ padding: "8px 16px", border: `1.5px solid ${isSel ? T.black : T.border}`, background: isSel ? T.black : "#fff", color: isSel ? "#fff" : T.black, fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 8, transition: "all .15s" }}>
                      {sz?.name ?? sId}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {variantItem?.description && (
            <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 18 }}>{variantItem.description}</p>
          )}

          {variantItem?.dimensions && (variantItem.dimensions.height || variantItem.dimensions.width || variantItem.dimensions.length || variantItem.dimensions.netWeight) && (
            <div style={{ marginBottom: 18, padding: "12px 14px", background: HEART.bg, borderRadius: 12, border: `1px solid ${HEART.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim, marginBottom: 8 }}>DIMENSIONS</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", fontSize: 12, color: T.black }}>
                {variantItem.dimensions.height && <span>H {variantItem.dimensions.height}{variantItem.dimensions.unit}</span>}
                {variantItem.dimensions.width && <span>W {variantItem.dimensions.width}{variantItem.dimensions.unit}</span>}
                {variantItem.dimensions.length && <span>L {variantItem.dimensions.length}{variantItem.dimensions.unit}</span>}
                {variantItem.dimensions.netWeight && <span>{variantItem.dimensions.netWeight}{variantItem.dimensions.weightUnit}</span>}
              </div>
            </div>
          )}

          {(getVariantMode(variantItem) === null || selColor !== null) && (
            relevantStock > 0 && relevantStock <= 5 ? (
              <p style={{ fontSize: 11, fontWeight: 700, color: T.red, marginBottom: 10 }}>Only {relevantStock} left</p>
            ) : relevantStock <= 0 ? (
              <p style={{ fontSize: 11, fontWeight: 700, color: T.mutedDim, marginBottom: 10 }}>Out of stock</p>
            ) : null
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim }}>QTY</span>
            <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: HEART.bg }}>
              <button onClick={() => setQty(v => Math.max(1, v - 1))} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: T.red }}>−</button>
              <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
              <button onClick={() => setQty(v => Math.min(relevantStock || 1, v + 1))} disabled={qty >= relevantStock} style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: qty >= relevantStock ? "not-allowed" : "pointer", fontSize: 16, color: T.red, opacity: qty >= relevantStock ? 0.4 : 1 }}>+</button>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(false)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed", opacity: canContinue ? 1 : 0.45 }}>
            Add to Bag
          </button>
          <button disabled={!canContinue} onClick={() => handleVariantContinue(true)}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: "none", background: canContinue ? GRAD_H : "#C8C8C8", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canContinue ? "pointer" : "not-allowed" }}>
            Buy Now
          </button>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={closeProductModal}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={e => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>{content}</motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }} style={{ width: 460, borderRadius: 20, overflow: "hidden", boxShadow: "0 32px 80px rgba(27,14,6,.35)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  const LocationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.8)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={e => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(27,14,6,.5)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD_H, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.black }}>Location Disabled</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>Enable location in your browser settings to see local products.</p>
            <button onClick={() => setLocationModalOpen(false)} style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>Got it</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const BottomTabs = () => isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Home", active: true, action: () => navigate(`/ProductsHome/${userId}`) },
        { icon: <ShoppingBasket size={22} />, label: "Stores", active: false, action: () => navigate("/Stores") },
        { label: "Cart", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartItems.length > 0 && <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>}
            </div>
          ), action: goToCheckout },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/glamourclientdashboard/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map(t => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`} style={{ position: "relative" }}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : T.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null;

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark />

      <div style={S.content}>
        <header style={S.headerTop}>
          <HeaderBar />
        </header>

        <MobileCategorySheet />

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && (
              <aside style={A.sidebar}>
                <CategoryPanelContent />
              </aside>
            )}
            <main style={{ minWidth: 0 }}>
              <VerticalTitleBanner />
              <div style={{ marginBottom: 4 }}><VerticalToggleBar /></div>
              <CategoryIconBar />
              <ShopByCategory />
              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            <VerticalTitleBanner />
            <VerticalToggleBar />
            <CategoryIconBar />
            <div style={{ marginTop: 16 }}><ShopByCategory /></div>
            {renderProducts(true)}
          </div>
        )}

        <VariantModal />
        <LocationModal />
        <BottomTabs />
      </div>
    </div>
  );
};

export default ProductsHome;